import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Keyboard } from 'swiper';
import 'swiper/css';
import { Helmet } from 'react-helmet';
import { WordPressPost } from '../types/wordpress';
import { fetchPosts, fetchPostById, fetchRelatedPosts, checkForNewContent, mergeNewPosts } from '../services/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BackgroundEffects from './BackgroundEffects';
import AudioSubtitlePlayer from './AudioSubtitlePlayer';
import BackgroundMusicPlayer from './BackgroundMusicPlayer';
import HashtagSection from './HashtagSection';
import { tagService } from '../services/tagService';
import { useSwipe } from './SwipeProvider';
import MobileTopAdSenseBanner from './MobileTopAdSenseBanner';
import { processContentForDisplay } from '../utils/textUtils';
import { useLanguageContext } from '../contexts/LanguageContext';

// ✅ NEW: Shared mobile navigation component for consistent alignment
import MobileNavigationButton from './MobileNavigationButton';

// ✅ REMOVED: Between-posts native ads per user request

// TikTokFeed Props Interface
interface TikTokFeedProps {
  mode?: 'normal' | 'tag' | 'related'; // Normal feed, tag-filtered feed, or related posts feed
  tagId?: number; // Tag ID for filtering (tag mode)
  tagName?: string; // Tag name for display (tag mode)
  tagSlug?: string; // Tag slug for URL navigation (tag mode)
  onFinished?: () => void; // Callback when all posts are viewed (tag/related mode)
}

// Tag Context Interface for navigation state
interface TagContext {
  tagId: number;
  tagName: string;
  tagSlug: string;
  sourcePostId: number;
  // Enhanced: Pre-loaded posts for faster initialization
  tagPosts?: WordPressPost[];
  totalTagPosts?: number;
}

// Related Context Interface for navigation state
interface RelatedContext {
  sourcePostId: number;
  sourcePostTitle: string;
  sourcePostTags: number[];
  targetPostId: number;
  // Pre-loaded related posts for faster initialization
  relatedPosts?: WordPressPost[];
  totalRelatedPosts?: number;
  contextType: 'related_posts';
}

// Platform detection utilities
const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isAndroid = () => {
  if (typeof window === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768;
};

// Platform-specific Swiper configurations
const getSwiperConfig = () => {
  const baseConfig = {
    modules: [Mousewheel, Keyboard],
    direction: "vertical" as const,
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    allowTouchMove: true,
    touchReleaseOnEdges: false,
    style: { height: '100%' }
  };

  if (isIOS()) {
    // iOS optimizations - sử dụng cấu hình tương tự Android nhưng với iOS-specific tweaks
    return {
      ...baseConfig,
      speed: 350, // Slightly faster than Android for iOS responsiveness
      resistanceRatio: 0.4, // Balanced resistance for smooth feel
      threshold: 6, // Lower threshold for responsive touch
      touchStartPreventDefault: true, // Prevent iOS scroll bounce
      touchMoveStopPropagation: false, // Allow natural touch behavior
      touchRatio: 1.1, // Slightly more sensitive for iOS
      longSwipesRatio: 0.4, // Easier long swipes
      longSwipesMs: 250, // Balanced timing
      followFinger: true, // Follow finger movement
      shortSwipes: true, // Enable short swipes
      freeMode: false, // Disable free mode for consistent behavior
      momentum: true, // Enable momentum
      momentumRatio: 1, // Natural momentum
      momentumVelocityRatio: 1, // Natural velocity
      momentumBounce: false, // Disable bounce for iOS smoothness
      mousewheel: false, // Disable mousewheel on iOS
      // Enhanced iOS smoothness
      touchAngle: 45, // Allow slight angle tolerance
      preventInteractionOnTransition: false, // Allow interaction during transition
      watchSlidesProgress: true, // Watch slide progress for smoother animations
      updateOnWindowResize: true, // Update on resize for better responsiveness
      virtual: false
    };
  } else if (isAndroid()) {
    // Android optimizations - enhanced from current working config
    return {
      ...baseConfig,
      speed: 380, // Slightly faster for better responsiveness
      resistanceRatio: 0.4, // Reduced resistance for smoother feel
      threshold: 7, // Slightly lower threshold for better responsiveness
      touchStartPreventDefault: false, // Allow native Android behaviors
      touchMoveStopPropagation: false,
      touchRatio: 1.05, // Slightly more sensitive
      longSwipesRatio: 0.4, // Easier long swipes
      longSwipesMs: 280, // Faster timing
      followFinger: true,
      shortSwipes: true,
      freeMode: false,
      momentum: true,
      momentumRatio: 1,
      momentumVelocityRatio: 1,
      momentumBounce: true, // Keep bounce on Android
      mousewheel: false, // Disable mousewheel on mobile
      // Enhanced Android smoothness
      touchAngle: 45, // Allow slight angle tolerance
      preventInteractionOnTransition: false, // Allow interaction during transition
      watchSlidesProgress: true, // Watch slide progress for smoother animations
      updateOnWindowResize: true, // Update on resize for better responsiveness
      virtual: false
    };
  } else {
    // Desktop/other platforms - optimized for mouse and keyboard
    return {
      ...baseConfig,
      speed: 400,
      resistanceRatio: 0.5,
      threshold: 8,
      touchStartPreventDefault: false,
      mousewheel: true, // Enable mousewheel on desktop
      touchRatio: 1,
      followFinger: true,
      momentum: true,
      // Enhanced desktop experience
      touchAngle: 45,
      preventInteractionOnTransition: false,
      watchSlidesProgress: true,
      updateOnWindowResize: true,
      virtual: false
    };
  }
};

// 🚀 SILENT PRELOADING: No visual animations needed

// Styled components
const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000;
  overflow: hidden;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// ✅ REMOVED: Individual styled components replaced with shared MobileNavigationButton
// This ensures consistent alignment and prevents future positioning issues



const Overlay = styled.div`
  position: absolute;
  left: 0; 
  right: 0; 
  bottom: 0;
  z-index: 2;
  background: transparent;
  padding: 12px 18px 90px 18px; /* ✅ FIXED: Reduced bottom padding from 120px to 90px (-30px to move content up) */
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  width: 100vw;
  box-sizing: border-box;
  padding-bottom: max(90px, env(safe-area-inset-bottom, 60px)); /* ✅ FIXED: Reduced safe area from 90px+30px to 60px (-30px) */
`;

const ContentBackground = styled.div`
  background: rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  backdrop-filter: blur(4px);
  
  /* ✅ NEW: Maximum height constraint for content section (8 lines) */
  max-height: calc(1.4em * 8); /* 8 lines with 1.4em line-height */
  overflow: hidden;
`;

// ✅ ENHANCED: Tags section with intelligent height overflow handling
const TagsBackground = styled.div`
  /* ✅ UPDATED: Remove background since each tag has its own background */
  background: transparent;
  border-radius: 0;
  padding: 0;
  margin-bottom: 12px;
  position: relative; /* ✅ NEW: For overflow mask */
  
  /* ✅ ENHANCED: Smart height limit that hides overflowing tags */
  max-height: calc(2.2em * 2 + 8px); /* 2 lines with proper tag height + gap */
  overflow: hidden;
  
  /* ✅ NEW: Flexbox layout for tags */
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px; /* Space between tags */
  line-height: 1.2;
  
  /* ✅ ENHANCED: Improved fade-out mask for overflowing content */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 12px; /* Increased for better visibility */
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.2)); /* Stronger fade */
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1; /* Ensure it's above tags */
  }
  
  /* ✅ ENHANCED: Show fade mask when content overflows */
  &.has-overflow::after {
    opacity: 1;
  }
  
  /* ✅ NEW: Visual indicator for overflowing tags */
  &.has-overflow::before {
    content: '···';
    position: absolute;
    bottom: 2px;
    right: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-weight: bold;
    z-index: 2;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }
`;

const PostTitle = styled.div`
  color: #fff;
  font-size: 1.45em;
  font-weight: 700;
  margin: 0 0 10px 0;
  text-align: left;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
`;

const Desc = styled.div`
  color: #fff;
  font-size: 1.08em;
  margin: 0;
  text-align: left;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  display: block;
  overflow: visible;
  text-overflow: unset;
  max-height: none;
  line-height: 1.4em;
`;

const CenterIcon = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: opacity 0.3s;
  pointer-events: none;
  
  svg {
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
  }
`;

const SafeAreaWrapper = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  position: relative;
  overflow: hidden;
  padding-top: env(safe-area-inset-top, 0);
  padding-bottom: env(safe-area-inset-bottom, 0);
  box-sizing: border-box;
`;

const SubtitleText = styled.p`
  color: white;
  font-size: 16px;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 8px;
  border-radius: 4px;
  display: inline-block;
  margin: 0 auto;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9);
`;

const SpeedToast = styled.div<{ show: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 18px;
  font-weight: bold;
  z-index: 15;
  opacity: ${({ show }) => show ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '⚡';
    margin-right: 8px;
    font-size: 16px;
  }
`;

// ✅ REMOVED: Mobile refresh indicators per user request



// TikTok Video Component (immersive) - Simplified without horizontal swipe
const TikTokVideo: React.FC<{ 
  post: WordPressPost; 
  active: boolean;
  onPausedChange?: (paused: boolean) => void;
  playbackRate?: number;
  onSpeedChange?: () => void;
  showSpeedToast?: boolean;
  speedToastValue?: string;
}> = React.memo(({ 
  post, 
  active, 
  onPausedChange, 
  playbackRate = 1.0, 
  onSpeedChange, 
  showSpeedToast = false,
  speedToastValue = '1X'
}) => {
  // UI state
  const [currentSubtitle, setCurrentSubtitle] = useState('');
  const [paused, setPaused] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [isSharing, setIsSharing] = useState(false); // Prevent duplicate shares
  const playIconTimeoutRef = useRef<number | null>(null);
  const shareTimeoutRef = useRef<number | null>(null);

  // Notify parent component about pause state changes
  useEffect(() => {
    if (onPausedChange) {
      onPausedChange(paused);
    }
  }, [paused, onPausedChange]);

  // Cleanup timeouts when component unmounts
  useEffect(() => {
    return () => {
      // ✅ MEMORY LEAK FIX: Cleanup all timeouts on unmount
      if (playIconTimeoutRef.current) {
        clearTimeout(playIconTimeoutRef.current);
        playIconTimeoutRef.current = null;
      }
      if (shareTimeoutRef.current) {
        clearTimeout(shareTimeoutRef.current);
        shareTimeoutRef.current = null;
      }
    };
  }, []); // ✅ FIX: Empty dependency array to only run on unmount
  
  // Tap pause/play
  const handleTap = () => {
    if (!active) return;
    setPaused(p => {
      const next = !p;
      if (next) {
        setShowPlayIcon(true);
        // Clear previous timeout if exists
        if (playIconTimeoutRef.current) {
          clearTimeout(playIconTimeoutRef.current);
        }
        playIconTimeoutRef.current = window.setTimeout(() => setShowPlayIcon(false), 700);
      }
      return next;
    });
  };

  // Clean HTML content
  const cleanHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  const title = post.title.rendered ? cleanHtml(post.title.rendered) : '';
  // 🌍 USE ENHANCED API: mobileContent for mobile, cleanContent for fallback
  const rawContent = post.mobileContent || post.cleanContent || (post.excerpt.rendered ? cleanHtml(post.excerpt.rendered) : '');
  
  // 📱 CONTENT PROCESSING: Format sentences + mobile truncation (frontend only, API remains full)
  // Mobile TikTok uses plain text rendering, so no HTML conversion needed
  const excerpt = processContentForDisplay(rawContent, isMobile(), 200, false);
  // ✅ LAZY AUDIO: Only pass audioUrl when post is actually active to prevent requests
  const audioUrl = (active && post.audioUrl) ? post.audioUrl : undefined;

  // Enhanced Share with debounce protection
  const handleShare = () => {
    // Prevent duplicate shares within 2 seconds
    if (isSharing) {
      console.log('[TikTokVideo] Share blocked - already sharing');
      return;
    }

    setIsSharing(true);
    console.log(`[TikTokVideo] === SHARE BUTTON CLICKED ===`);
    
    // Try native share first (mobile)
    if (navigator.share) {
      navigator.share({
        title: title,
        text: excerpt.length > 160 ? excerpt.substring(0, 160) + '...' : excerpt,
        url: `${window.location.origin}/post/${post.id}/${post.slug}.html`,
      })
      .then(() => {
        console.log('[TikTokVideo] Native share successful');
      })
      .catch((error) => {
        console.log('[TikTokVideo] Native share failed, falling back to clipboard:', error);
        copyToClipboard(`${window.location.origin}/post/${post.id}/${post.slug}.html`);
      })
      .finally(() => {
        // Reset sharing state after 2 seconds to prevent rapid clicks
        shareTimeoutRef.current = window.setTimeout(() => {
          setIsSharing(false);
        }, 2000);
      });
    } else {
      // Fallback to copy to clipboard
      copyToClipboard(`${window.location.origin}/post/${post.id}/${post.slug}.html`);
      // Reset sharing state after 2 seconds
      shareTimeoutRef.current = window.setTimeout(() => {
        setIsSharing(false);
      }, 2000);
    }
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      console.log('[TikTokVideo] Clipboard copy successful');
      alert('✅ Link copied to clipboard!');
    } catch (err) {
      console.error('[TikTokVideo] Clipboard copy failed:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      console.log('[TikTokVideo] Fallback clipboard copy successful');
      alert('✅ Link copied to clipboard!');
    }
  };

  const currentUrl = `${window.location.origin}/post/${post.id}/${post.slug}.html`;
  const mainImageUrl = post.imageUrls && post.imageUrls.length > 0 ? post.imageUrls[0] : 'https://cdn.infonium.org/img/logo_infonium.png';
  const description = excerpt.length > 160 ? excerpt.substring(0, 160) + '...' : excerpt;

  return (
    <SafeAreaWrapper>
      <VideoContainer style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
        <div onClick={handleTap} style={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* Dynamic Meta Tags */}
          {active && (
            <Helmet>
              <title>{title} | Infonium</title>
              <meta name="description" content={description} />
              <meta property="og:title" content={title} />
              <meta property="og:description" content={description} />
              <meta property="og:type" content="article" />
              <meta property="og:url" content={currentUrl} />
              <meta property="og:image" content={mainImageUrl} />
              <meta property="og:site_name" content="Infonium" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={title} />
              <meta name="twitter:description" content={description} />
              <meta name="twitter:image" content={mainImageUrl} />
              <meta name="twitter:site" content="@infonium" />
              <link rel="canonical" href={currentUrl} />
            </Helmet>
          )}
          
          {/* Background effects */}
          <BackgroundEffects 
            imageUrls={post.imageUrls || []}
            active={active}
            postId={post.id}
          />
          
          <CenterIcon visible={showPlayIcon || paused}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="20,15 20,45 45,30" fill="white" fillOpacity="0.9" />
            </svg>
          </CenterIcon>
          
          {/* Speed Toast Notification */}
          <SpeedToast show={showSpeedToast}>
            {speedToastValue}
          </SpeedToast>
          
          {/* Overlay content */}
          <Overlay>
            {/* Subtitle */}
            {currentSubtitle && (
              <SubtitleText style={{ marginBottom: 8 }}>{currentSubtitle}</SubtitleText>
            )}
            
            {/* Title outside background */}
            <PostTitle>{title}</PostTitle>
            
            {/* ✅ NEW: Content section with 8-line height limit */}
            <ContentBackground>
              <Desc>
                {excerpt}
              </Desc>
            </ContentBackground>
            
            {/* ✅ NEW: Separate tags section with 2-line height limit */}
            {((post.tagDetails && post.tagDetails.length > 0) || (post.tags && post.tags.length > 0)) && (
              <TagsBackgroundWrapper postId={post.id}>
                <HashtagSection 
                  tagDetails={post.tagDetails ? post.tagDetails.sort((a, b) => b.id - a.id).slice(0, 6) : undefined} 
                  tagIds={post.tags ? [...post.tags].sort((a, b) => b - a).slice(0, 6) : undefined}
                  mode="mobile-inline" 
                />
              </TagsBackgroundWrapper>
            )}
            
            {/* Desktop hashtags section - keep for desktop */}
            {((post.tagDetails && post.tagDetails.length > 0) || (post.tags && post.tags.length > 0)) && (
              <HashtagSection 
                tagDetails={post.tagDetails ? post.tagDetails.sort((a, b) => b.id - a.id).slice(0, 6) : undefined} 
                tagIds={post.tags ? [...post.tags].sort((a, b) => b - a).slice(0, 6) : undefined}
                mode="desktop-section"
              />
            )}
          </Overlay>
          
          {/* Audio subtitle player */}
          <AudioSubtitlePlayer
            audioUrl={audioUrl}
            subtitleUrl={post.subtitleUrl}
            active={active}
            onSubtitleChange={setCurrentSubtitle}
            paused={paused}
            onPausedChange={setPaused}
            playbackRate={playbackRate}
            postId={post.id}
          />
          
          {/* ✅ FIXED: Mobile navigation controls with consistent alignment */}
          <>
            <MobileNavigationButton 
              position="home" 
              href="/" 
              title="Go to Homepage"
              aria-label="Navigate to homepage"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"/>
              </svg>
            </MobileNavigationButton>

            <MobileNavigationButton 
              position="share" 
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleShare(); }} 
              disabled={isSharing} 
              title={isSharing ? 'Sharing...' : 'Share article'}
              aria-label={isSharing ? 'Sharing article' : 'Share this article'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.12833C7.54305 8.43356 6.7382 8 6 8C4.34315 8 3 9.34315 3 11C3 12.6569 4.34315 14 6 14C6.7382 14 7.54305 13.5664 8.08261 12.8717L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.2618 14 16.457 14.4336 15.9174 15.1283L8.97733 11.3706C8.99229 11.2492 9 11.1255 9 11C9 10.8745 8.99229 10.7508 8.97733 10.6294L15.9174 6.87167C16.457 7.56644 17.2618 8 18 8Z" fill="white"/>
              </svg>
            </MobileNavigationButton>

            <MobileNavigationButton 
              position="speed" 
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onSpeedChange && onSpeedChange(); }} 
              title={`Speed: ${playbackRate}x`}
              aria-label={`Change playback speed (current: ${playbackRate}x)`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 2v11h3v9l7-12h-4l4-8z" fill="currentColor"/>
              </svg>
            </MobileNavigationButton>
          </>
        </div>
      </VideoContainer>
    </SafeAreaWrapper>
  );
});

// ✅ ENHANCED: Component wrapper for tags with intelligent overflow detection
const TagsBackgroundWrapper: React.FC<{ 
  children: React.ReactNode;
  postId: number;
}> = ({ children, postId }): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const { scrollHeight, clientHeight } = containerRef.current;
        const hasOverflow = scrollHeight > clientHeight;
        
        if (hasOverflow) {
          containerRef.current.classList.add('has-overflow');
          console.log(`[TagsBackground] 📏 Post ${postId}: Overflow detected (${scrollHeight}px > ${clientHeight}px)`);
        } else {
          containerRef.current.classList.remove('has-overflow');
        }
      }
    };

    // Check overflow after render
    const timeoutId = setTimeout(checkOverflow, 100); // Delay to ensure rendering is complete
    
    // ✅ ENHANCED: Use ResizeObserver for better responsiveness
    if (containerRef.current && 'ResizeObserver' in window) {
      resizeObserverRef.current = new ResizeObserver(() => {
        checkOverflow();
      });
      resizeObserverRef.current.observe(containerRef.current);
    }
    
    // Fallback: window resize listener
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [children, postId]);

  return (
    <TagsBackground ref={containerRef}>
      {children}
    </TagsBackground>
  );
};

// TikTok Feed Component
const TikTokFeed: React.FC<TikTokFeedProps> = ({ 
  mode = 'normal', 
  tagId, 
  tagName, 
  tagSlug,
  onFinished 
}) => {
  const { id: urlId, postId } = useParams<{ id: string; postId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentPost } = useSwipe();
  const { getCurrentLanguageCategoryId, isInitialized: isLanguageInitialized } = useLanguageContext();
  const languageCategoryId = getCurrentLanguageCategoryId();
  
  // Check for context from navigation state (from TagPage or RelatedPage mobile click)
  const navigationTagContext = location.state?.tagContext as TagContext | undefined;
  const navigationRelatedContext = location.state?.relatedContext as RelatedContext | undefined;
  
  const isTagContextInjected = navigationTagContext && !tagId; // Only use if not already in tag mode
  const isRelatedContextInjected = navigationRelatedContext && !tagId; // Only use if not already in tag mode
  
  // Determine effective context and mode
  const effectiveTagId = tagId || (isTagContextInjected ? navigationTagContext.tagId : undefined);
  const effectiveTagName = tagName || (isTagContextInjected ? navigationTagContext.tagName : undefined);
  const effectiveTagSlug = tagSlug || (isTagContextInjected ? navigationTagContext.tagSlug : undefined);
  
  // Determine effective mode based on injected contexts
  let effectiveMode = mode;
  if (tagId || isTagContextInjected) {
    effectiveMode = 'tag';
  } else if (isRelatedContextInjected) {
    effectiveMode = 'related';
  }
  
  console.log('[TikTokFeed] 🔄 Context Detection (Enhanced):', {
    propMode: mode,
    effectiveMode,
    propTagId: tagId,
    effectiveTagId,
    isTagContextInjected,
    isRelatedContextInjected,
    navigationTagContext: navigationTagContext ? {
      tagId: navigationTagContext.tagId,
      tagName: navigationTagContext.tagName,
      sourcePostId: navigationTagContext.sourcePostId,
      hasPreloadedPosts: !!navigationTagContext.tagPosts
    } : null,
    navigationRelatedContext: navigationRelatedContext ? {
      sourcePostId: navigationRelatedContext.sourcePostId,
      targetPostId: navigationRelatedContext.targetPostId,
      hasPreloadedPosts: !!navigationRelatedContext.relatedPosts,
      totalRelatedPosts: navigationRelatedContext.totalRelatedPosts
    } : null,
    urlId,
    postId
  });
  
  // ✅ CRITICAL DEBUG: Add extensive post ID detection logging
  console.log('[TikTokFeed] 🎯 POST ID DETECTION ANALYSIS:', {
    urlId: urlId,
    urlIdAsNumber: urlId ? parseInt(urlId, 10) : null,
    postId: postId,
    postIdAsNumber: postId ? parseInt(postId, 10) : null,
    navigationTagContextSourcePostId: navigationTagContext?.sourcePostId,
    isTagContextInjected,
    effectiveMode,
    currentPath: location.pathname,
    allLocationState: location.state
  });
  
  console.log('[TikTokFeed] 🔍 Debug URL Parameters (DETAILED):', {
    urlId,
    postId,
    effectiveMode,
    isTagContextInjected,
    currentPath: location.pathname,
    locationState: location.state,
    shouldProcessUrlId: effectiveMode === 'normal' && urlId && !isTagContextInjected,
    shouldLoadSpecificPost: effectiveMode === 'normal' && urlId,
    actualRouteParameters: { urlId, postId },
    navigationTagContext
  });
  
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const [initialSlide, setInitialSlide] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [globalPaused, setGlobalPaused] = useState(false); // Track global pause state
  const [playbackRate, setPlaybackRate] = useState(1.0); // Playback speed from 0.5 to 2.0
  const [showSpeedToast, setShowSpeedToast] = useState(false); // Show speed notification
  const [speedToastValue, setSpeedToastValue] = useState('1X'); // Speed display value
  
  // NEW: Auto-refresh states for mobile (silent mode)
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  
  // 🎯 SMART FEED: Time tracking and related posts switching
  const [currentPostViewTime, setCurrentPostViewTime] = useState(0);
  const [hasTriggeredRelatedMode, setHasTriggeredRelatedMode] = useState(false);
  const [isRelatedMode, setIsRelatedMode] = useState(false);
  const [injectedPostIds, setInjectedPostIds] = useState<Set<number>>(new Set()); // Track which posts had injection
  const [currentPostForRelated, setCurrentPostForRelated] = useState<WordPressPost | null>(null);
  
  const pageRef = useRef(1);
  const loadedPages = useRef(new Set([1]));
  const firstLoadRef = useRef(true);
  const prevUrlId = useRef<string | undefined>(undefined);
  const isNavigating = useRef(false);
  const swiperRef = useRef<any>(null);
  const allPostsRef = useRef<WordPressPost[]>([]); // Keep all posts for navigation
  const perPage = 20;
  // Get platform-specific Swiper configuration
  const swiperConfig = getSwiperConfig();

  // ✅ MEMORY LEAK FIX: Track timeouts for cleanup
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const finishedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speedToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const viewTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const relatedSwitchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Speed control handlers
  const handleSpeedChange = () => {
    const speeds = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
    const speedLabels = ['0.5X', '0.75X', '1X', '1.25X', '1.5X', '2X'];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const nextSpeed = speeds[nextIndex];
    const nextLabel = speedLabels[nextIndex];
    
    setPlaybackRate(nextSpeed);
    setSpeedToastValue(nextLabel);
    setShowSpeedToast(true);
    
    // Clear previous timeout
    if (speedToastTimeoutRef.current) {
      clearTimeout(speedToastTimeoutRef.current);
    }
    
    // Hide toast after 1.5 seconds
    speedToastTimeoutRef.current = setTimeout(() => {
      setShowSpeedToast(false);
    }, 1500);
  };



  // ✅ NEW: Global audio cleanup on component mount
  useEffect(() => {
    console.log('[TikTokFeed] 🧹 Component mounted, triggering global audio cleanup');
    
    // Trigger global audio cleanup when component mounts
    if (typeof window !== 'undefined' && window.GlobalAudioManager) {
      try {
        const globalAudioManager = window.GlobalAudioManager.getInstance();
        globalAudioManager.cleanupVirtualScrolling();
      } catch (error) {
        console.error('[TikTokFeed] ❌ Error during mount cleanup:', error);
      }
    }

    // Cleanup on unmount
    return () => {
      console.log('[TikTokFeed] 🧹 Component unmounting, triggering final audio cleanup');
      
      // Clean up all timers
      if (viewTimeIntervalRef.current) {
        clearInterval(viewTimeIntervalRef.current);
      }
      if (relatedSwitchTimeoutRef.current) {
        clearTimeout(relatedSwitchTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (speedToastTimeoutRef.current) {
        clearTimeout(speedToastTimeoutRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      if (finishedTimeoutRef.current) {
        clearTimeout(finishedTimeoutRef.current);
      }
      
      if (typeof window !== 'undefined' && window.GlobalAudioManager) {
        try {
          const globalAudioManager = window.GlobalAudioManager.getInstance();
          globalAudioManager.stopAllAudio();
          globalAudioManager.cleanupVirtualScrolling();
        } catch (error) {
          console.error('[TikTokFeed] ❌ Error during unmount cleanup:', error);
        }
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Enhanced data loading với tag mode support
  useEffect(() => {
    // ✅ Wait for language detection before loading data
    if (!isLanguageInitialized) {
      console.log('[TikTokFeed] ⏳ Waiting for language detection...');
      return;
    }

    // Skip nếu đang trong quá trình navigate từ swipe
    if (isNavigating.current) {
      isNavigating.current = false;
      prevUrlId.current = urlId;
      return;
    }
    
    let shouldReset = false;
    if (firstLoadRef.current) {
      shouldReset = true;
      firstLoadRef.current = false;
    } else if (effectiveMode === 'normal') {
      // Normal mode URL logic - reset when URL changes
      if (prevUrlId.current !== urlId) {
        shouldReset = true;
        console.log('[TikTokFeed] 🔄 URL changed, triggering reset:', {
          prevUrlId: prevUrlId.current,
          currentUrlId: urlId,
          shouldReset
        });
      }
    } else if (effectiveMode === 'tag') {
      // Tag mode always reset on mount
      shouldReset = true;
    } else if (effectiveMode === 'related') {
      // Related mode always reset on mount
      shouldReset = true;
    }
    
    if (shouldReset) {
      setLoading(true);
      const idNum = urlId ? parseInt(urlId, 10) : undefined;
      let initialPost: WordPressPost | null = null;
      let fetchedPosts: WordPressPost[] = [];
      let postsList: WordPressPost[] = [];
      let initialIdx = 0;
      
      (async () => {
        if (effectiveMode === 'tag' && effectiveTagId) {
          // Tag mode: load posts by tag
          console.log('[TikTokFeed] Loading posts for tag:', { tagId: effectiveTagId, tagName: effectiveTagName });
          
          // Handle specific postId - check both URL and injected context
          let targetPostId: number | null = null;
          if (postId) {
            // URL-based post ID (from /tag/slug/post/id)
            targetPostId = parseInt(postId, 10);
            console.log('[TikTokFeed] 🎯 Target post from URL postId:', targetPostId);
          } else if (isTagContextInjected && navigationTagContext?.sourcePostId) {
            // Injected context post ID (from TagPage mobile click)
            targetPostId = navigationTagContext.sourcePostId;
            console.log('[TikTokFeed] 🎯 Target post from injected context:', targetPostId);
          } else if (urlId && !isNaN(parseInt(urlId, 10))) {
            // URL-based post ID (from /post/id/slug.html with tag context)
            targetPostId = parseInt(urlId, 10);
            console.log('[TikTokFeed] 🎯 Target post from URL urlId:', targetPostId);
          }
          
          if (targetPostId && !isNaN(targetPostId)) {
            // 🚀 TAG PARALLEL LOADING STRATEGY: Load song song cả tag feed và specific post
            console.log('[TikTokFeed] 🎯 TAG PARALLEL LOADING: Fetching both tag feed and specific post simultaneously');
            
            const [tagPosts, specificPost] = await Promise.all([
              tagService.getPostsByTag(effectiveTagId, 1, perPage),  // Load tag feed
              fetchPostById(targetPostId)                           // Load specific post
            ]);
            
            console.log('[TikTokFeed] 📊 Tag parallel loading results:', {
              tagPostsCount: tagPosts?.length || 0,
              specificPostFound: !!specificPost,
              specificPostId: specificPost?.id || 'none',
              specificPostTitle: specificPost?.title?.rendered?.substring(0, 50) || 'No title',
              tagId: effectiveTagId
            });
            
            if (specificPost && tagPosts) {
              // Verify specific post belongs to this tag
              const postHasTag = specificPost.tags && specificPost.tags.includes(effectiveTagId);
              
              if (postHasTag) {
                // 🎯 TAG OVERLAY STRATEGY: Đè specific post lên đầu tag feed
                console.log('[TikTokFeed] 📌 TAG OVERLAY: Placing specific post on top of tag feed');
                
                // Remove specific post from tag posts if it exists to avoid duplicates
                const filteredTagPosts = tagPosts.filter(p => p.id !== specificPost.id);
                
                // Create final posts array with specific post first (overlay strategy)
                postsList = [specificPost, ...filteredTagPosts];
                initialIdx = 0; // Always show specific post first
                
                console.log('[TikTokFeed] ✅ TAG PARALLEL LOADING SUCCESS:', {
                  strategy: 'TAG_OVERLAY',
                  specificPostId: specificPost.id,
                  specificPostTitle: specificPost.title?.rendered?.substring(0, 50) || 'No title',
                  totalPosts: postsList.length,
                  initialIndex: initialIdx,
                  tagPostsFiltered: tagPosts.length - filteredTagPosts.length,
                  finalStructure: 'specific post + filtered tag posts',
                  tagId: effectiveTagId
                });
                
                fetchedPosts = postsList; // For consistent variable naming
              } else {
                // Post doesn't belong to tag, use tag posts only
                console.warn('[TikTokFeed] ⚠️ Specific post does not belong to tag, using tag posts only');
                fetchedPosts = tagPosts;
                postsList = tagPosts;
                initialIdx = 0;
              }
            } else if (tagPosts) {
              // Fallback to tag posts only if specific post failed
              console.log('[TikTokFeed] ⚠️ Specific post failed, using tag posts only');
              fetchedPosts = tagPosts;
              postsList = tagPosts;
              initialIdx = 0;
            } else {
              console.error('[TikTokFeed] ❌ Both tag parallel requests failed');
              fetchedPosts = [];
              postsList = [];
            }
          } else {
            // No specific post requested, just load tag feed
            console.log('[TikTokFeed] 🏷️ Loading tag feed only (no specific post)');
            fetchedPosts = await tagService.getPostsByTag(effectiveTagId, 1, perPage);
            postsList = fetchedPosts;
            initialIdx = 0;
          }
        } else if (effectiveMode === 'related' && isRelatedContextInjected && navigationRelatedContext) {
          // Related mode: load related posts for the source post
          console.log('[TikTokFeed] 🔗 Loading related posts:', { 
            sourcePostId: navigationRelatedContext.sourcePostId,
            targetPostId: navigationRelatedContext.targetPostId,
            hasPreloadedPosts: !!navigationRelatedContext.relatedPosts
          });
          
          // Use pre-loaded posts if available for faster initialization
          if (navigationRelatedContext.relatedPosts && navigationRelatedContext.relatedPosts.length > 0) {
            console.log('[TikTokFeed] 🚀 Using pre-loaded related posts for faster initialization');
            fetchedPosts = navigationRelatedContext.relatedPosts;
            postsList = fetchedPosts;
            
            // Find the target post in the list to set as initial slide
            const targetPostIndex = fetchedPosts.findIndex((p: WordPressPost) => p.id === navigationRelatedContext.targetPostId);
            initialIdx = targetPostIndex >= 0 ? targetPostIndex : 0;
            
            console.log('[TikTokFeed] ✅ Pre-loaded related posts used:', {
              totalPosts: fetchedPosts.length,
              targetPostId: navigationRelatedContext.targetPostId,
              targetPostIndex: targetPostIndex,
              initialIndex: initialIdx
            });
          } else {
            // Fallback: Load related posts via API
            console.log('[TikTokFeed] 📡 Loading related posts via API (fallback)');
            try {
              const languageCategoryId = getCurrentLanguageCategoryId();
              const relatedPosts = await fetchRelatedPosts(
                navigationRelatedContext.sourcePostId, 
                perPage,
                languageCategoryId || undefined
              );
              
              if (relatedPosts && relatedPosts.length > 0) {
                fetchedPosts = relatedPosts;
                postsList = relatedPosts;
                
                // Find the target post in the list
                const targetPostIndex = relatedPosts.findIndex((p: WordPressPost) => p.id === navigationRelatedContext.targetPostId);
                initialIdx = targetPostIndex >= 0 ? targetPostIndex : 0;
                
                console.log('[TikTokFeed] ✅ Related posts loaded via API:', {
                  totalPosts: relatedPosts.length,
                  targetPostId: navigationRelatedContext.targetPostId,
                  targetPostIndex: targetPostIndex,
                  initialIndex: initialIdx
                });
              } else {
                console.warn('[TikTokFeed] ⚠️ No related posts found - returning empty for consistent mobile behavior');
                // NO FALLBACK to general posts API for mobile single post consistency
                fetchedPosts = [];
                postsList = [];
                initialIdx = 0;
              }
            } catch (error) {
              console.error('[TikTokFeed] ❌ Error loading related posts:', error);
              // NO FALLBACK to general posts API for mobile single post consistency
              fetchedPosts = [];
              postsList = [];
              initialIdx = 0;
            }
          }
        } else {
          // Normal mode: load general posts
          console.log('[TikTokFeed] 📰 Normal mode loading:', { urlId, idNum, hasUrlId: !!idNum });
          
          if (idNum) {
            // ✅ IMPROVED STRATEGY: Load specific post first, then check if it exists in feed
            console.log('[TikTokFeed] 🎯 SEQUENTIAL LOADING: Fetching specific post first to prevent audio conflicts');
            
            try {
              // Step 1: Load specific post first
              const specificPost = await fetchPostById(idNum);
              
              if (specificPost) {
                console.log('[TikTokFeed] ✅ Specific post found:', {
                  id: specificPost.id,
                  title: specificPost.title?.rendered?.substring(0, 50) || 'No title',
                  hasAudio: !!specificPost.audioUrl
                });
                
                // ✅ MOBILE SINGLE POST FIX: Load ONLY related posts, NO general posts
                console.log('[TikTokFeed] 🎯 Mobile single post detected - progressive loading enabled');
                
                // ✅ PROGRESSIVE LOADING: Set main post immediately for instant rendering
                postsList = [specificPost];
                initialIdx = 0;
                initialPost = specificPost;
                fetchedPosts = postsList;
                
                console.log('[TikTokFeed] ✅ Main post loaded - rendering immediately!', {
                  postId: specificPost.id,
                  title: specificPost.title?.rendered?.substring(0, 50) || 'No title',
                  strategy: 'Progressive Mobile Loading'
                });
                
                // ✅ ASYNC RELATED POSTS: Load in background without blocking UI
                // This will update the posts array after initial render
                const relatedPostsTimer = setTimeout(async () => {
                  try {
                    console.log('[TikTokFeed] 🔗 Loading related posts asynchronously...');
                    
                    const relatedPosts = await fetchRelatedPosts(
                      specificPost.id,
                      perPage - 1, // Load perPage-1 since we already have the specific post
                      languageCategoryId || undefined
                    );
                    
                    if (relatedPosts && relatedPosts.length > 0) {
                      // Filter out current post if it appears in related results
                      const filteredRelatedPosts = relatedPosts.filter(p => p.id !== specificPost.id);
                      
                      // ✅ PROGRESSIVE UPDATE: Add related posts to existing main post
                      const finalPostsList = [specificPost, ...filteredRelatedPosts];
                      
                      console.log('[TikTokFeed] ✅ Related posts loaded asynchronously:', {
                        strategy: 'PROGRESSIVE_MOBILE_SINGLE_POST',
                        specificPostId: specificPost.id,
                        specificPostTitle: specificPost.title?.rendered?.substring(0, 50) || 'No title',
                        relatedPostsCount: filteredRelatedPosts.length,
                        totalPosts: finalPostsList.length,
                        initialIndex: initialIdx,
                        loadingType: 'Background (non-blocking)'
                      });
                      
                      // ✅ SAFE STATE UPDATE: Check if component is still mounted and posts haven't changed
                      setPosts(currentPosts => {
                        // Only update if we still have just the main post
                        if (currentPosts.length === 1 && currentPosts[0].id === specificPost.id) {
                          allPostsRef.current = finalPostsList;
                          setTotalPostsLoaded(finalPostsList.length);
                          return finalPostsList;
                        }
                        return currentPosts; // Keep existing if structure changed
                      });
                      
                    } else {
                      console.log('[TikTokFeed] ⚠️ No related posts found - main post already rendered');
                    }
                  } catch (relatedError) {
                    console.error('[TikTokFeed] ❌ Related posts loading failed (non-blocking):', relatedError);
                    // Don't throw - main post is already rendered
                  }
                }, 100); // Small delay to ensure main post renders first
                
                // ✅ PROGRESSIVE LOADING: Set related mode flag
                if (specificPost && specificPost.tags && specificPost.tags.length > 0) {
                  console.log('🔗 [SmartFeed] Progressive loading - related posts will load asynchronously');
                  setHasTriggeredRelatedMode(true);
                }
                
              } else {
                // Specific post not found for mobile single post
                console.log('[TikTokFeed] ⚠️ Specific post not found - no fallback for mobile single post consistency');
                postsList = [];
                fetchedPosts = postsList;
                initialIdx = 0;
              }
              
            } catch (error) {
              console.error('[TikTokFeed] ❌ Error in mobile single post loading:', error);
              // NO FALLBACK to general posts for mobile single post consistency
              postsList = [];
              fetchedPosts = [];
              initialIdx = 0;
            }
            
          } else {
            // No specific post requested, just load general feed
            console.log('[TikTokFeed] 📰 Fetching general posts feed only...');
            fetchedPosts = await fetchPosts(1, perPage, undefined, undefined, false, languageCategoryId || undefined);
            console.log('[TikTokFeed] 📰 General posts fetched:', { 
              count: fetchedPosts.length, 
              firstPostId: fetchedPosts[0]?.id,
              lastPostId: fetchedPosts[fetchedPosts.length - 1]?.id
            });
            
            postsList = fetchedPosts;
            initialIdx = 0;
          }
        }
        
        setPosts(postsList);
        allPostsRef.current = postsList;
        setTotalPostsLoaded(postsList.length);
        setPage(1);
        loadedPages.current = new Set([1]);
        setSwiperIndex(initialIdx);
        setInitialSlide(initialIdx);
        
        console.log('[TikTokFeed] ✅ Posts loaded:', { 
          mode: effectiveMode, 
          count: postsList.length, 
          initialIdx,
          tagId: effectiveTagId,
          tagName: effectiveTagName,
          tagSlug: effectiveTagSlug
        });
        
        // NEW: Set initial last update time for auto-refresh
        if (postsList.length > 0 && effectiveMode === 'normal') {
          const latestDate = postsList[0].date;
          setLastUpdateTime(latestDate);
          console.log('[TikTokFeed] ⏰ Initial last update time set:', latestDate);
        }
        
      })().catch(error => {
        console.error('[TikTokFeed] Error loading posts:', error);
        setPosts([]);
      }).finally(() => {
        setLoading(false);
      });
    }
    prevUrlId.current = urlId;
  }, [urlId, effectiveMode, effectiveTagId, effectiveTagName, effectiveTagSlug, postId, isTagContextInjected, isLanguageInitialized, languageCategoryId]);

  // NEW: Auto-refresh setup for mobile (only for normal mode)
  useEffect(() => {
    const setupMobileAutoRefresh = () => {
      if (effectiveMode !== 'normal' || !lastUpdateTime || posts.length === 0) {
        return;
      }
      
      console.log('[TikTokFeed] 🔄 Setting up mobile auto-refresh (60s interval)');
      
      const checkForUpdates = async () => {
        if (isRefreshing || loading) {
          return;
        }
        
        try {
          setIsRefreshing(true);
          console.log('[TikTokFeed] 🔍 Checking for new content (mobile)...');
          
          const result = await checkForNewContent(lastUpdateTime, perPage, languageCategoryId || undefined);
          
          if (result.hasNewContent) {
            console.log(`[TikTokFeed] 🆕 Found ${result.totalNewPosts} new posts on mobile!`);
            
            // For mobile, we silently merge new posts at the beginning
            const { mergedPosts, addedCount } = mergeNewPosts(posts, result.newPosts);
            
            if (addedCount > 0) {
              setPosts(mergedPosts);
              allPostsRef.current = mergedPosts;
              
              // Update last update time
              const latestDate = result.newPosts[0].date;
              setLastUpdateTime(latestDate);
              
              // ✅ SILENT INJECTION: No notifications, just seamlessly add to bottom
              console.log(`[TikTokFeed] ✅ Silently injected ${addedCount} new posts at bottom of feed`);
            }
          } else {
            console.log('[TikTokFeed] 📭 No new content found (mobile)');
          }
        } catch (error) {
          console.error('[TikTokFeed] ❌ Error checking for updates (mobile):', error);
        } finally {
          setIsRefreshing(false);
        }
      };
      
      // Start periodic checking
      refreshIntervalRef.current = setInterval(checkForUpdates, 60000); // 60 seconds
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    };
    
    // Only setup auto-refresh for normal mode with data
    if (effectiveMode === 'normal' && posts.length > 0 && lastUpdateTime) {
      const cleanup = setupMobileAutoRefresh();
      return cleanup;
    }
  }, [effectiveMode, posts, lastUpdateTime, isRefreshing, loading]);

  // 🎯 SMART FEED: Time tracking for current post viewing
  useEffect(() => {
    // Only track time in normal mode and when not already in related mode
    if (effectiveMode !== 'normal' || isRelatedMode || hasTriggeredRelatedMode) {
      return;
    }

    // Start tracking viewing time for current post
    if (posts.length > 0 && swiperIndex < posts.length) {
      const currentPost = posts[swiperIndex];
      setCurrentPostForRelated(currentPost);
      setCurrentPostViewTime(0);
      setHasTriggeredRelatedMode(false); // Reset for each new post

      console.log('🕒 [SmartFeed] Starting time tracking for post:', {
        postId: currentPost.id,
        title: currentPost.title?.rendered?.substring(0, 30) + '...',
        tags: currentPost.tags?.length || 0,
        hasRelatedMode: isRelatedMode,
        alreadyInjected: injectedPostIds.has(currentPost.id)
      });

      // Clear any existing timer
      if (viewTimeIntervalRef.current) {
        clearInterval(viewTimeIntervalRef.current);
      }

      // Track viewing time every second
      viewTimeIntervalRef.current = setInterval(() => {
        setCurrentPostViewTime(prevTime => {
          const newTime = prevTime + 1;
          
          // 🚫 REMOVED: Auto-injection after 20s viewing (UX violation)
          // User reported: "Trình duyệt load api related post > Tự động bị thay thế bài đang xem với bài đầu tiên trong related post"
          // This auto-injection behavior violates user experience principles - NEVER auto-replace content user is actively viewing
          // Related posts should only be available through manual user action (swipe gestures, explicit navigation, etc.)
          
          if (newTime === 20 && !hasTriggeredRelatedMode && currentPost.tags && currentPost.tags.length > 0 && !injectedPostIds.has(currentPost.id)) {
            console.log('🚫 [SmartFeed] 20s viewing detected but auto-injection DISABLED for better UX:', {
              postId: currentPost.id,
              viewTime: newTime,
              tags: currentPost.tags,
              reason: 'AUTO_INJECTION_DISABLED_FOR_UX'
            });
            
            // Just mark as triggered to prevent repeated logging, but don't inject
            setHasTriggeredRelatedMode(true);
            setInjectedPostIds(prev => new Set([...prev, currentPost.id]));
            
            // Clear the tracking timer since we've acknowledged the 20s milestone
            if (viewTimeIntervalRef.current) {
              clearInterval(viewTimeIntervalRef.current);
              viewTimeIntervalRef.current = null;
            }
            
            // NO INJECTION - User maintains control of their viewing experience
            console.log('✅ [SmartFeed] User experience preserved - no auto-content-replacement');
          }
          
          return newTime;
        });
      }, 1000); // Update every second
    }

    // Cleanup on dependency change
    return () => {
      if (viewTimeIntervalRef.current) {
        clearInterval(viewTimeIntervalRef.current);
        viewTimeIntervalRef.current = null;
      }
    };
  }, [swiperIndex, posts, effectiveMode, isRelatedMode, hasTriggeredRelatedMode, injectedPostIds]);

  // 🚫 DISABLED: Inject related posts below current post (UX violation - auto-replacement)
  // This function was causing automatic content replacement after 20s viewing
  // User reported: "Tự động bị thay thế bài đang xem với bài đầu tiên trong related post"
  // Keeping function for potential future manual trigger implementation
  const injectRelatedPostsBelow = async (currentPost: WordPressPost) => {
    console.warn('⚠️ [SmartFeed] injectRelatedPostsBelow called but DISABLED for UX protection');
    return; // Early exit - function disabled
    if (!currentPost.tags || currentPost.tags.length === 0) {
      console.warn('🚫 [SmartFeed] No tags available for related posts injection');
      return;
    }

    try {
      console.log('🔄 [SmartFeed] Injecting related posts below current post:', {
        postId: currentPost.id,
        tags: currentPost.tags,
        tagCount: currentPost.tags.length,
        currentFeedLength: allPostsRef.current.length,
        currentIndex: swiperIndex
      });

      // Get top 6 highest ID tags (this logic is already in fetchRelatedPosts)
      const topTags = [...currentPost.tags]
        .sort((a, b) => b - a) // Sort descending (highest ID first)  
        .slice(0, 6); // Take top 6 tags

      console.log('🎯 [SmartFeed] Using top 6 highest ID tags:', topTags);

      // Load related posts using the top 6 tags with language filtering
      const languageCategoryId = getCurrentLanguageCategoryId();
      const relatedPosts = await fetchRelatedPosts(
        currentPost.id,
        Math.max(perPage * 2, 25), // At least 25 related posts
        languageCategoryId || undefined
      );

      if (relatedPosts && relatedPosts.length > 0) {
        // Filter out current post and any existing posts in feed
        const existingIds = new Set(allPostsRef.current.map(p => p.id));
        const filteredRelated = relatedPosts.filter(p => !existingIds.has(p.id));

        if (filteredRelated.length > 0) {
          // Calculate injection position (right after current post)
          const currentIndex = swiperIndex;
          const beforeInjection = allPostsRef.current.slice(0, currentIndex + 1);
          const afterInjection = allPostsRef.current.slice(currentIndex + 1);
          
          // Inject related posts right after current post
          const newFeed = [...beforeInjection, ...filteredRelated, ...afterInjection];

          console.log('✅ [SmartFeed] Related posts injected successfully:', {
            originalCount: relatedPosts.length,
            filteredCount: filteredRelated.length,
            injectionPosition: currentIndex + 1,
            oldFeedLength: allPostsRef.current.length,
            newFeedLength: newFeed.length,
            addedPosts: filteredRelated.length,
            currentPostId: currentPost.id,
            mode: 'INJECT_RELATED_POSTS'
          });

          // Update feed with injected posts - use stable update to prevent Swiper jumps
          const currentSwiperIndex = swiperIndex; // Capture current position
          
          // Update ref first to prevent useEffect sync conflicts
          allPostsRef.current = newFeed;
          
          // Then update state with position preservation
          setPosts(prevPosts => {
            // Only update if we're still at the same position (user hasn't swiped)
            if (swiperIndex === currentSwiperIndex) {
              return newFeed;
            }
            // If user swiped during injection, keep old posts to prevent confusion
            return prevPosts;
          });
          
          setIsRelatedMode(true); // Mark as having related content
          setHasMore(true); // Re-enable infinite scroll for remaining content
          
          // Ensure Swiper stays at current position after injection
          if (swiperRef.current && typeof swiperRef.current.slideTo === 'function') {
            // Use setTimeout to ensure DOM has updated
            setTimeout(() => {
              if (swiperRef.current && swiperIndex === currentSwiperIndex) {
                swiperRef.current.slideTo(currentSwiperIndex, 0); // 0ms = instant
              }
            }, 50);
          }
          
          console.log('🎯 [SmartFeed] Related posts injected below current post, position preserved at index:', currentSwiperIndex);
        } else {
          console.warn('⚠️ [SmartFeed] All related posts already exist in feed, no injection needed');
        }
      } else {
        console.warn('⚠️ [SmartFeed] No related posts found for injection');
      }
    } catch (error) {
      console.error('❌ [SmartFeed] Error injecting related posts:', error);
    }
  };

  // 🎯 Load related posts for direct URL access
  const loadRelatedPostsForDirectAccess = async (specificPost: WordPressPost, fallbackPosts: WordPressPost[]) => {
    if (!specificPost.tags || specificPost.tags.length === 0) {
      console.warn('🚫 [SmartFeed] No tags available for direct access related posts');
      return;
    }

    try {
      console.log('🔄 [SmartFeed] Loading related posts for direct URL access:', {
        postId: specificPost.id,
        tags: specificPost.tags,
        tagCount: specificPost.tags.length,
        fallbackCount: fallbackPosts.length
      });

      // ✅ DEPRECATED: This function is no longer needed as related posts are loaded in main useEffect
      // All related posts loading is now handled in the main useEffect to prevent duplicate API calls
      console.log('⚠️ [SmartFeed] loadRelatedPostsForDirectAccess deprecated - related posts already loaded in main effect');
      return; // Exit early to prevent duplicate API calls
    } catch (error) {
      console.error('❌ [SmartFeed] Error loading related posts for direct access:', error);
    }
  };

  // ✅ COMPLETE REWRITE: Simple and reliable sync approach
  const lastSlideIndex = useRef(-1);
  
  const handleSlideChange = (swiper: any) => {
    const currentIndex = swiper.realIndex;
    const currentPost = allPostsRef.current[currentIndex];
    
    // ✅ SINGLE SOURCE OF TRUTH: Only use swiperIndex, remove virtualIndex complexity
    setSwiperIndex(currentIndex);
    
    // ✅ CONTROLLED LOGGING: Only log on actual slide changes
    if (lastSlideIndex.current !== currentIndex) {
      console.log('[TikTokFeed] 🔄 SlideChange:', {
        from: lastSlideIndex.current,
        to: currentIndex,
        postId: currentPost?.id,
        postTitle: currentPost?.title?.rendered?.substring(0, 25) || 'No title'
      });
      lastSlideIndex.current = currentIndex;
    }

    // ✅ CRITICAL: Verify data consistency (minimal logging)
    if (!currentPost) {
      console.error('[TikTokFeed] ❌ CRITICAL: Current post not found for index:', currentIndex);
      return;
    }

    // ✅ UPDATE CURRENT POST CONTEXT: For RelatedPage integration
    setCurrentPost({
      id: currentPost.id,
      title: currentPost.title?.rendered || '',
      tags: currentPost.tags || [],
      slug: currentPost.slug || ''
    });

    // ✅ DEBOUNCED URL UPDATE: Prevent race conditions during fast scrolling
    updateURLWithDebounce(currentPost, currentIndex);

    // ✅ ENHANCED CRITICAL FIX: Simple state verification
    if (posts.length !== allPostsRef.current.length) {
      console.log('[TikTokFeed] 🔄 RECOVERY: Syncing posts array');
      setPosts([...allPostsRef.current]);
    }

    // ✅ NEW: Notify GlobalAudioManager about post switch
    if (currentPost && typeof window !== 'undefined' && window.GlobalAudioManager) {
      try {
        const globalAudioManager = window.GlobalAudioManager.getInstance();
        globalAudioManager.onPostSwitch(currentPost.id);
        
        // Also trigger force cleanup event for any lingering AudioSubtitlePlayer instances
        const forceCleanupEvent = new CustomEvent('forceAudioCleanup', {
          detail: { 
            reason: 'slide_change', 
            newPostId: currentPost.id,
            slideIndex: currentIndex,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(forceCleanupEvent);
      } catch (error) {
        console.error('[TikTokFeed] ❌ Error during audio cleanup:', error);
      }
    }
    
    // 🚀 ENHANCED PRELOADING: Load more posts with intelligent prediction
    const totalPosts = allPostsRef.current.length;
    const remainingPosts = totalPosts - currentIndex - 1; // -1 because currentIndex is 0-based
    
    // Smart thresholds based on user behavior and content availability
    const PRELOAD_THRESHOLDS = {
      AGGRESSIVE: 8,  // When user swipes quickly
      NORMAL: 5,      // Default threshold 
      CONSERVATIVE: 3 // When loading is slow or errors
    };
    
    // Determine threshold based on current conditions
    let threshold = PRELOAD_THRESHOLDS.NORMAL;
    
    // Use aggressive preloading for normal browsing
    if (hasMore && !isLoadingMore && totalPosts > 10) {
      threshold = PRELOAD_THRESHOLDS.AGGRESSIVE;
    }
    
    // Use conservative preloading if we had recent errors or slow loading
    if (totalPosts < 20) {
      threshold = PRELOAD_THRESHOLDS.CONSERVATIVE;
    }
    
    // 🎯 MAIN PRELOADING LOGIC
    if (remainingPosts <= threshold && hasMore && !isLoadingMore && effectiveMode === 'normal') {
      console.log('[TikTokFeed] 🚀 Smart preloading triggered:', {
        currentIndex,
        totalPosts,
        remainingPosts,
        threshold: `${threshold} (${remainingPosts <= PRELOAD_THRESHOLDS.AGGRESSIVE ? 'AGGRESSIVE' : 
                      remainingPosts <= PRELOAD_THRESHOLDS.NORMAL ? 'NORMAL' : 'CONSERVATIVE'})`,
        reason: remainingPosts <= 3 ? 'CRITICAL - User approaching end' : 
                remainingPosts <= 5 ? 'NORMAL - Preload for smooth experience' : 
                'AGGRESSIVE - Early preload for seamless flow'
      });
      
      loadMorePosts();
    }
    
    // 📱 SILENT PRELOADING: Background process only (no UI feedback)
    if (remainingPosts <= 2 && hasMore && !isLoadingMore) {
      // Log for debugging but don't show user anything
      console.log('[TikTokFeed] 📱 Near end - preloading silently in background');
    }
  };

  // ✅ DEBOUNCED URL UPDATE: Prevent race conditions during fast scrolling
  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const updateURLWithDebounce = (currentPost: any, currentIndex: number) => {
    if (!currentPost) return;
    
    // Clear previous timeout to debounce rapid calls
    if (urlUpdateTimeoutRef.current) {
      clearTimeout(urlUpdateTimeoutRef.current);
    }
    
    // Debounce URL updates to prevent race conditions
    urlUpdateTimeoutRef.current = setTimeout(() => {
      updateURLImmediately(currentPost, currentIndex);
    }, 150); // 150ms debounce - balance between responsiveness and stability
  };

  // ✅ IMMEDIATE URL UPDATE: Internal function for actual navigation
  const updateURLImmediately = (currentPost: any, currentIndex: number) => {
    if (!currentPost) return;
    
    try {
      const expectedUrl = effectiveMode === 'tag' && effectiveTagId && effectiveTagSlug && !isTagContextInjected
        ? `/tag/${effectiveTagSlug}/post/${currentPost.id}`
        : `/post/${currentPost.id}/${currentPost.slug}.html`;
      
      // Set navigation flag to prevent loops
      isNavigating.current = true;
      
      if (effectiveMode === 'tag' && effectiveTagId && effectiveTagSlug && !isTagContextInjected) {
        navigate(expectedUrl, { replace: true });
        console.log('[TikTokFeed] 🔗 URL updated (tag mode):', expectedUrl, 'Post:', currentPost.id);
      } else {
        navigate(expectedUrl, { 
          replace: true,
          state: isTagContextInjected ? {
            tagContext: {
              tagId: effectiveTagId,
              tagName: effectiveTagName,
              tagSlug: effectiveTagSlug,
              sourcePostId: currentPost.id
            }
          } : undefined
        });
        console.log('[TikTokFeed] 🔗 URL updated (normal mode):', expectedUrl, 'Post:', currentPost.id);
      }
      
      // Reset navigation flag after a short delay
      setTimeout(() => {
        isNavigating.current = false;
      }, 100);
    } catch (error) {
      console.error('[TikTokFeed] ❌ Error updating URL:', error);
      isNavigating.current = false;
    }
  };

  // ✅ No cleanup needed - immediate URL updates

  // 🚀 ENHANCED: Load more posts function (silent background process)
  const loadMorePosts = async () => {
    if (isLoadingMore || !hasMore || effectiveMode !== 'normal') {
      console.log('[TikTokFeed] ⏭️ Skipping load more:', {
        isLoadingMore,
        hasMore,
        mode: effectiveMode
      });
      return;
    }

    setIsLoadingMore(true);
    const nextPage = pageRef.current + 1;
    
    // Check if page already loaded
    if (loadedPages.current.has(nextPage)) {
      console.log('[TikTokFeed] ⚠️ Page already loaded:', nextPage);
      setIsLoadingMore(false);
      return;
    }

    try {
      console.log('[TikTokFeed] 📥 Loading page:', nextPage);
      
      const newPosts = await fetchPosts(nextPage, perPage, undefined, undefined, false, languageCategoryId || undefined);
      
      if (newPosts && newPosts.length > 0) {
        // Filter out duplicates based on post ID
        const existingIds = new Set(allPostsRef.current.map(p => p.id));
        const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
        
        if (uniqueNewPosts.length > 0) {
          const updatedPosts = [...allPostsRef.current, ...uniqueNewPosts];
          
          // ✅ SIMPLIFIED: Update allPostsRef first, then sync states
          allPostsRef.current = updatedPosts;
          setPosts(updatedPosts);
          setTotalPostsLoaded(updatedPosts.length);
          setPage(nextPage);
          pageRef.current = nextPage;
          loadedPages.current.add(nextPage);
          
          // Check if we have more posts to load
          const hasMorePosts = newPosts.length === perPage;
          setHasMore(hasMorePosts);
          
          console.log('[TikTokFeed] ✅ Posts loaded:', {
            newPostsCount: uniqueNewPosts.length,
            totalPostsNow: updatedPosts.length,
            currentPage: nextPage,
            hasMorePosts
          });
          
          // 🚀 SILENT: Preload completed successfully (no UI feedback)
        } else {
          console.log('[TikTokFeed] ⚠️ All new posts were duplicates');
          loadedPages.current.add(nextPage);
          setPage(nextPage);
          pageRef.current = nextPage;
        }
      } else {
        console.log('[TikTokFeed] 📭 No more posts available');
        setHasMore(false);
      }
      
    } catch (error) {
      console.error('[TikTokFeed] ❌ Error loading more posts:', error);
      // Silent error handling - no UI feedback needed
    } finally {
      setIsLoadingMore(false);
    }
  };

  // ✅ SIMPLIFIED: Transition end - minimal verification
  const handleTransitionEnd = (swiper: any) => {
    const currentIndex = swiper.realIndex;
    const currentPost = allPostsRef.current[currentIndex];
    
    // ✅ SIMPLIFIED: Just verify post exists in allPostsRef
    if (!currentPost) {
      console.error('[TikTokFeed] ❌ CRITICAL: No current post found for transition end:', {
        currentIndex,
        allPostsRefLength: allPostsRef.current.length
      });
      return;
    }
    
    // ✅ REDUCED LOGGING: Only log in dev mode
    if (process.env.NODE_ENV === 'development') {
      console.log('[TikTokFeed] 🏁 TransitionEnd:', currentPost.id);
    }
    
    // ✅ FIXED: Auto-redirect logic with SmartFeed injection protection
    if ((effectiveMode === 'tag' || effectiveMode === 'related') && currentIndex === allPostsRef.current.length - 1 && !hasMore) {
      console.log(`[TikTokFeed] Reached end of ${effectiveMode} posts`);
      
      // ✅ CRITICAL FIX: Don't redirect if this is SmartFeed injection (user-initiated)
      const isSmartFeedInjection = isRelatedMode && injectedPostIds.size > 0;
      
      if ((isTagContextInjected || isRelatedContextInjected) && !isSmartFeedInjection) {
        console.log(`[TikTokFeed] ${effectiveMode} context injected (navigation-based) - redirecting to main feed`);
        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/', { replace: true });
        }, 1000);
      } else if (isSmartFeedInjection) {
        console.log(`[TikTokFeed] 🚫 SmartFeed injection detected - NOT redirecting to preserve user experience`);
        // User can continue browsing, no forced redirect
      } else if (onFinished) {
        console.log(`[TikTokFeed] Original ${effectiveMode} mode - triggering onFinished`);
        finishedTimeoutRef.current = setTimeout(() => {
          onFinished();
        }, 1000);
      }
    }
  };

  // Performance optimization constants
  const MAX_POSTS_IN_MEMORY = 100; // Maximum posts to keep in memory
  const POSTS_BUFFER_SIZE = 30; // Keep posts around current position
  const CLEANUP_THRESHOLD = 150; // Start cleanup when exceeding this
  
  // Performance optimization constants
  const [totalPostsLoaded, setTotalPostsLoaded] = useState(0);
  
  // 🚀 PRELOADING: Silent background process (no UI feedback)

  // ✅ STABILIZED: Posts sync with injection protection
  useEffect(() => {
    if (allPostsRef.current.length > 0) {
      // ✅ INJECTION PROTECTION: Don't sync during SmartFeed injection to prevent jumps
      const isInjecting = injectedPostIds.size > 0 && hasTriggeredRelatedMode;
      
      if (posts.length !== allPostsRef.current.length && !isInjecting) {
        console.log('[TikTokFeed] 🔄 Syncing posts length (non-injection):', allPostsRef.current.length);
        setPosts([...allPostsRef.current]);
      } else if (isInjecting) {
        console.log('[TikTokFeed] 🚫 Skipping sync during SmartFeed injection to prevent position jumps');
      }
    }
  }, [allPostsRef.current.length, injectedPostIds.size, hasTriggeredRelatedMode]); // ✅ Injection-aware dependencies

  // ✅ MEMORY LEAK FIX: Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
      if (finishedTimeoutRef.current) {
        clearTimeout(finishedTimeoutRef.current);
        finishedTimeoutRef.current = null;
      }
      if (speedToastTimeoutRef.current) {
        clearTimeout(speedToastTimeoutRef.current);
        speedToastTimeoutRef.current = null;
      }
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
        urlUpdateTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <Container>
      {/* ✅ FIXED BANNER: Always at top of screen, independent of post swipes */}
      <MobileTopAdSenseBanner 
        isPostVisible={true} 
        postId={posts[swiperIndex]?.id} 
      />
      
      {/* Background Music Player - low priority, chạy sau khi content đã load */}
      <BackgroundMusicPlayer active={!loading && posts.length > 0} volume={0.10} globalPaused={globalPaused} />
      
      {/* ✅ REMOVED: Mobile auto-refresh indicators per user request */}

      
      <Swiper
        key={initialSlide}
        {...swiperConfig}
        onSlideChange={handleSlideChange}
        onTransitionEnd={handleTransitionEnd}
        initialSlide={initialSlide}
        ref={swiperRef}
        virtual={false}
      >
{posts.map((post: any, index: number) => {
          // ✅ CRITICAL FIX: Validate post data before rendering
          if (!post || !post.id) {
            console.error('[TikTokFeed] ❌ Invalid post data at index:', index, post);
            return null;
          }

          const slides = [];
          
          // Add the main post slide
          slides.push(
            <SwiperSlide 
              key={post.id} 
              style={{ height: '100dvh', padding: 0, margin: 0 }}
            >
              <TikTokVideo 
                post={post} 
                active={index === swiperIndex} 
                onPausedChange={(paused) => {
                  // Only update global pause state for the active video
                  if (index === swiperIndex) {
                    setGlobalPaused(paused);
                    
                    // ✅ DEBUG: Log active post data
                    console.log('[TikTokFeed] 🎵 Active post changed:', {
                      index,
                      swiperIndex,
                      postId: post.id,
                      postTitle: post.title?.rendered?.substring(0, 30) || 'No title',
                      paused
                    });
                  }
                }}
                playbackRate={playbackRate}
                onSpeedChange={handleSpeedChange}
                showSpeedToast={showSpeedToast}
                speedToastValue={speedToastValue}
              />
            </SwiperSlide>
          );

          // ✅ REMOVED: Between-posts native ads per user request

          return slides;
        }).flat()}
      </Swiper>
      {/* 🚀 SILENT PRELOADING: No UI indicators - completely background process */}
      
      {/* Memory usage indicator (dev mode) */}
      {process.env.NODE_ENV === 'development' && posts.length > 0 && (
        <div style={{ 
          position: 'absolute', 
          top: 10, 
          left: 10, 
          background: 'rgba(0,0,0,0.7)', 
          color: '#fff', 
          padding: '8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          zIndex: 10 
        }}>
          📊 Posts: {posts.length}/{totalPostsLoaded} | Pos: {swiperIndex + 1}
        </div>
      )}
    </Container>
  );
};

// Keyframes for pulse animation
const pulseAnimation = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

// Inject CSS for animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseAnimation;
  document.head.appendChild(style);
}

export default TikTokFeed;
